import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getProspectos, createProspecto, updateProspecto, updateEstatusProspecto,
  getRamos, getUsuarios, getAgencias,
  importarProspectosExcel, createSeguimiento, getSeguimientosByProspecto,
  convertirProspecto,
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/ui/Modal';
import { Phone, PhoneOff } from 'lucide-react';

const ESTATUS_COLORS: Record<string, string> = {
  Nuevo:      'bg-gray-100 text-gray-700',
  Contactado: 'bg-blue-100 text-blue-700',
  Interesado: 'bg-yellow-100 text-yellow-700',
  Cotizado:   'bg-purple-100 text-purple-700',
  Convertido: 'bg-green-100 text-green-700',
  Perdido:    'bg-red-100 text-red-700',
};

const ESTATUS_LIST = ['Nuevo','Contactado','Interesado','Cotizado','Convertido','Perdido'];
// Etiquetas visibles acordadas en la llamada con Daniel Santos (Referido →
// Interesado → En proceso → Cliente / Descartado). El valor real que viaja
// al backend no cambia, solo el texto que ve el agente.
const ESTATUS_LABELS: Record<string, string> = {
  Nuevo:      'Referido',
  Contactado: 'Contactado',
  Interesado: 'Interesado',
  Cotizado:   'En proceso',
  Convertido: 'Cliente',
  Perdido:    'Descartado',
};
const FACILIDAD_LIST = ['Baja', 'Media', 'Alta'];

const EMPTY_FORM = {
  nombre: '', telefono: '', email: '', edad: '', estadoCivil: '',
  numeroHijos: '', ocupacion: '', estiloDeVida: '', ingresoAproximado: '',
  centroDeInfluencia: '', relacionConAgente: '', canalCaptacion: '',
  ramoInteres: '', comoAbordarlo: '', notas: '', fechaProximoContacto: '',
  yaTieneSeguro: false, facilidadCita: '',
};

const EMPTY_FILTROS = { estatus: '', centroDeInfluencia: '', contactado: '', agenteId: '', agenciaId: '' };

export default function Prospectos() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.rol === 'Admin' || user?.rol === 'SuperAdmin';
  const isSuperAdmin = user?.rol === 'SuperAdmin';
  const canFilterByAgent = isAdmin || user?.rol === 'Gerente';

  const [prospectos, setProspectos] = useState<any[]>([]);
  const [ramos, setRamos] = useState<any[]>([]);
  const [agentes, setAgentes] = useState<any[]>([]);
  const [agencias, setAgencias] = useState<any[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [filtros, setFiltros] = useState(EMPTY_FILTROS);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState('');
  const [llamando, setLlamando] = useState<number | null>(null);
  const [bitacoraProspecto, setBitacoraProspecto] = useState<any>(null);
  const [bitacora, setBitacora] = useState<any[] | null>(null);
  const [conversionProspecto, setConversionProspecto] = useState<any>(null);
  const [conversionForm, setConversionForm] = useState({ producto: '', numeroPolizaCliente: '' });
  const [savingConversion, setSavingConversion] = useState(false);

  const load = (f = filtros) => {
    const params: any = {};
    if (f.estatus) params.estatus = f.estatus;
    if (f.centroDeInfluencia) params.centroDeInfluencia = f.centroDeInfluencia;
    if (f.contactado !== '') params.contactado = f.contactado === 'true';
    if (f.agenteId) params.agenteId = Number(f.agenteId);
    if (f.agenciaId) params.agenciaId = Number(f.agenciaId);
    getProspectos(params).then(res => setProspectos(
      // El backend serializa Estatus como número de enum (1=Nuevo..6=Perdido);
      // el resto de la UI (ESTATUS_LIST/COLORS/LABELS) usa el nombre en texto.
      res.data.map((p: any) => ({
        ...p,
        estatus: typeof p.estatus === 'number' ? ESTATUS_LIST[p.estatus - 1] : p.estatus,
      }))
    ));
  };

  useEffect(() => {
    getRamos().then(res => setRamos(res.data));
    if (canFilterByAgent) getUsuarios().then(res => setAgentes(res.data));
    if (isSuperAdmin) getAgencias().then(res => setAgencias(res.data));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(filtros); }, [filtros]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEstatus = async (p: any, estatus: string) => {
    if (estatus === 'Convertido') {
      setConversionProspecto(p);
      setConversionForm({ producto: p.ramoInteres || '', numeroPolizaCliente: '' });
      return;
    }
    try {
      await updateEstatusProspecto(p.id, estatus);
      setProspectos(prev => prev.map(x => x.id === p.id ? { ...x, estatus } : x));
    } catch {
      alert('No se pudo actualizar el estatus. Intenta de nuevo.');
    }
  };

  const submitConversion = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingConversion(true);
    try {
      await convertirProspecto(conversionProspecto.id, {
        producto: conversionForm.producto || undefined,
        numeroPolizaCliente: conversionForm.numeroPolizaCliente || undefined,
      });
      setProspectos(prev => prev.map(x => x.id === conversionProspecto.id
        ? { ...x, estatus: 'Convertido', ramoInteres: conversionForm.producto || x.ramoInteres }
        : x));
      setConversionProspecto(null);
    } finally {
      setSavingConversion(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setError('');
    setShowModal(true);
  };

  const openEdit = (p: any) => {
    setEditingId(p.id);
    setForm({
      nombre: p.nombre || '',
      telefono: p.telefono || '',
      email: p.email || '',
      edad: p.edad != null ? String(p.edad) : '',
      estadoCivil: p.estadoCivil || '',
      numeroHijos: p.numeroHijos != null ? String(p.numeroHijos) : '',
      ocupacion: p.ocupacion || '',
      estiloDeVida: p.estiloDeVida || '',
      ingresoAproximado: p.ingresoAproximado || '',
      centroDeInfluencia: p.centroDeInfluencia || '',
      relacionConAgente: p.relacionConAgente || '',
      canalCaptacion: p.canalCaptacion || '',
      ramoInteres: p.ramoInteres || '',
      comoAbordarlo: p.comoAbordarlo || '',
      notas: p.notas || '',
      fechaProximoContacto: p.fechaProximoContacto
        ? new Date(p.fechaProximoContacto).toISOString().slice(0, 16)
        : '',
      yaTieneSeguro: p.yaTieneSeguro ?? false,
      facilidadCita: p.facilidadCita || '',
    });
    setError('');
    setShowModal(true);
  };

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;
    setImporting(true);
    setImportMsg('');
    try {
      const res = await importarProspectosExcel(file);
      const { importados, omitidos } = res.data;
      setImportMsg(`✅ ${importados} prospecto(s) importado(s)${omitidos ? `, ${omitidos} omitido(s) por duplicado` : ''}`);
      load();
    } catch {
      setImportMsg('❌ No se pudo importar el archivo. Verifica el formato.');
    } finally {
      setImporting(false);
    }
  };

  const handleLlamar = async (p: any, resultado: 'Contestada' | 'No contestó') => {
    setLlamando(p.id);
    try {
      await createSeguimiento({ tipo: 'Llamada', resultado, prospectoId: p.id });
      if (bitacoraProspecto?.id === p.id) openBitacora(p);
    } catch {
      alert('No se pudo registrar la llamada. Intenta de nuevo.');
    } finally {
      setLlamando(null);
    }
  };

  const openBitacora = async (p: any) => {
    setBitacoraProspecto(p);
    setBitacora(null);
    const res = await getSeguimientosByProspecto(p.id);
    setBitacora(Array.isArray(res.data) ? res.data : []);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    const payload = {
      ...form,
      edad: form.edad ? Number(form.edad) : null,
      numeroHijos: form.numeroHijos ? Number(form.numeroHijos) : null,
      ingresoAproximado: form.ingresoAproximado || null,
      fechaProximoContacto: form.fechaProximoContacto || null,
      facilidadCita: form.facilidadCita || null,
    };
    try {
      if (editingId != null) {
        await updateProspecto(editingId, payload);
      } else {
        await createProspecto(payload);
      }
      setShowModal(false);
      setForm(EMPTY_FORM);
      load();
    } catch {
      setError('Error al guardar. Verifica los datos.');
    } finally {
      setSaving(false);
    }
  };

  const field = (key: Exclude<keyof typeof EMPTY_FORM, 'yaTieneSeguro'>) => ({
    value: form[key] as string,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm(f => ({ ...f, [key]: e.target.value })),
  });

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-2xl font-bold text-gray-800">Prospectos</h2>
        <div className="flex gap-2">
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImportFile} />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-700 disabled:opacity-60"
          >
            {importing ? 'Importando...' : '📥 Importar Excel'}
          </button>
          <button
            onClick={openCreate}
            className="bg-primary-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-primary-700"
          >
            + Nuevo prospecto
          </button>
        </div>
      </div>
      {importMsg && <p className="text-sm mb-4">{importMsg}</p>}

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select
          value={filtros.estatus}
          onChange={e => setFiltros(f => ({ ...f, estatus: e.target.value }))}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
        >
          <option value="">Todos los estatus</option>
          {ESTATUS_LIST.map(s => <option key={s} value={s}>{ESTATUS_LABELS[s]}</option>)}
        </select>
        <input
          type="text"
          placeholder="Centro de influencia..."
          value={filtros.centroDeInfluencia}
          onChange={e => setFiltros(f => ({ ...f, centroDeInfluencia: e.target.value }))}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
        />
        <select
          value={filtros.contactado}
          onChange={e => setFiltros(f => ({ ...f, contactado: e.target.value }))}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
        >
          <option value="">Ya le marcaron / no marcaron</option>
          <option value="true">Ya le marcaron</option>
          <option value="false">Sin marcar</option>
        </select>
        {isSuperAdmin && agencias.length > 0 && (
          <select
            value={filtros.agenciaId}
            onChange={e => setFiltros(f => ({ ...f, agenciaId: e.target.value, agenteId: '' }))}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
          >
            <option value="">Todas las agencias</option>
            {agencias.map((a: any) => (
              <option key={a.id} value={a.id}>{a.nombre}</option>
            ))}
          </select>
        )}
        {canFilterByAgent && agentes.length > 0 && (
          <select
            value={filtros.agenteId}
            onChange={e => setFiltros(f => ({ ...f, agenteId: e.target.value }))}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
          >
            <option value="">Todos los agentes</option>
            {agentes.map((a: any) => (
              <option key={a.id} value={a.id}>{a.nombre}</option>
            ))}
          </select>
        )}
        {(filtros.estatus || filtros.centroDeInfluencia || filtros.contactado || filtros.agenteId || filtros.agenciaId) && (
          <button
            onClick={() => setFiltros(EMPTY_FILTROS)}
            className="text-xs text-gray-500 hover:text-gray-700 underline"
          >
            Limpiar filtros
          </button>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
            <tr>
              <th className="px-4 py-3 text-left">Nombre</th>
              <th className="px-4 py-3 text-left">Teléfono</th>
              <th className="px-4 py-3 text-left">Producto</th>
              <th className="px-4 py-3 text-left">Fuente</th>
              <th className="px-4 py-3 text-left">Próximo contacto</th>
              <th className="px-4 py-3 text-left">¿Tiene seguro?</th>
              <th className="px-4 py-3 text-left">Facilidad cita</th>
              <th className="px-4 py-3 text-left">Estatus</th>
              <th className="px-4 py-3 text-left">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {prospectos.map(p => (
              <tr key={p.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-medium text-gray-800">
                  <button
                    onClick={() => navigate(`/prospectos/${p.id}`)}
                    className="hover:text-primary-600 hover:underline text-left"
                  >
                    {p.nombre}
                  </button>
                </td>
                <td className="px-4 py-3 text-gray-600">{p.telefono}</td>
                <td className="px-4 py-3 text-gray-600">{p.ramoInteres || '—'}</td>
                <td className="px-4 py-3 text-gray-600">{p.canalCaptacion || '—'}</td>
                <td className="px-4 py-3 text-gray-600">
                  {p.fechaProximoContacto
                    ? new Date(p.fechaProximoContacto).toLocaleDateString('es-MX')
                    : '—'}
                </td>
                <td className="px-4 py-3 text-gray-600">
                  {p.yaTieneSeguro == null ? '—' : p.yaTieneSeguro ? 'Sí' : 'No'}
                </td>
                <td className="px-4 py-3 text-gray-600">{p.facilidadCita || '—'}</td>
                <td className="px-4 py-3">
                  <select
                    value={p.estatus}
                    onChange={e => handleEstatus(p, e.target.value)}
                    className={`text-xs px-2 py-1 rounded-full border-0 font-medium ${ESTATUS_COLORS[p.estatus]}`}
                  >
                    {ESTATUS_LIST.map(s => <option key={s} value={s}>{ESTATUS_LABELS[s]}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleLlamar(p, 'Contestada')}
                      disabled={llamando === p.id}
                      title="Llamada contestada"
                      className="hover:scale-110 transition-transform disabled:opacity-40 flex items-center justify-center"
                    >
                      {llamando === p.id ? '⏳' : <Phone size={16} className="text-green-600" />}
                    </button>
                    <button
                      onClick={() => handleLlamar(p, 'No contestó')}
                      disabled={llamando === p.id}
                      title="No contestó / colgó"
                      className="hover:scale-110 transition-transform disabled:opacity-40 flex items-center justify-center"
                    >
                      <PhoneOff size={16} className="text-red-600" />
                    </button>
                    <button
                      onClick={() => openBitacora(p)}
                      title="Ver bitácora"
                      className="text-sm hover:scale-110 transition-transform"
                    >
                      📋
                    </button>
                  </div>
                  <button
                    onClick={() => openEdit(p)}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium mt-1"
                  >
                    Editar
                  </button>
                </td>
              </tr>
            ))}
            {prospectos.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-gray-400">
                  No hay prospectos registrados
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <Modal
          title={editingId != null ? 'Editar prospecto' : 'Nuevo prospecto'}
          onClose={() => setShowModal(false)}
          size="lg"
        >
          <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
            <div className="col-span-2 text-xs font-semibold text-gray-400 uppercase tracking-wider pt-1">
              Datos básicos
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
              <input type="text" {...field('nombre')} required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono *</label>
              <input type="tel" {...field('telefono')} required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input type="email" {...field('email')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Edad</label>
              <input type="number" {...field('edad')} min={1} max={120}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Estado civil</label>
              <select {...field('estadoCivil')} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="">Seleccionar...</option>
                {['Soltero','Casado','Divorciado','Viudo','Unión libre'].map(o =>
                  <option key={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Núm. hijos</label>
              <input type="number" {...field('numeroHijos')} min={0}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>

            <div className="col-span-2 text-xs font-semibold text-gray-400 uppercase tracking-wider pt-2">
              Perfil
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ocupación</label>
              <input type="text" {...field('ocupacion')} placeholder="Médico, contador, empresario..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ingreso aproximado</label>
              <input type="text" {...field('ingresoAproximado')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Estilo de vida</label>
              <input type="text" {...field('estiloDeVida')} placeholder="Deportivo, viajero, familiar..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Centro de influencia</label>
              <input type="text" {...field('centroDeInfluencia')} placeholder="Cámara de comercio, club deportivo..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Relación con agente</label>
              <input type="text" {...field('relacionConAgente')} placeholder="Amigo, familiar, conocido..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div className="flex items-center gap-2 pt-6">
              <input
                type="checkbox"
                id="yaTieneSeguro"
                checked={form.yaTieneSeguro}
                onChange={e => setForm(f => ({ ...f, yaTieneSeguro: e.target.checked }))}
                className="h-4 w-4"
              />
              <label htmlFor="yaTieneSeguro" className="text-sm font-medium text-gray-700">¿Ya tiene seguro?</label>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">¿Facilidad para cita?</label>
              <select {...field('facilidadCita')} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="">Seleccionar...</option>
                {FACILIDAD_LIST.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>

            <div className="col-span-2 text-xs font-semibold text-gray-400 uppercase tracking-wider pt-2">
              Captación
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fuente</label>
              <select {...field('canalCaptacion')} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="">Seleccionar...</option>
                {['Referido','Redes sociales','Llamada en frío','Visita','WhatsApp','Otro'].map(o =>
                  <option key={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Producto</label>
              <select {...field('ramoInteres')} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="">Seleccionar...</option>
                {ramos.map((r: any) => <option key={r.id} value={r.nombre}>{r.icono} {r.nombre}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Próximo contacto</label>
              <input type="datetime-local" {...field('fechaProximoContacto')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">¿Cómo abordarlo?</label>
              <input type="text" {...field('comoAbordarlo')} placeholder="Estrategia de acercamiento..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
              <textarea {...field('notas')} rows={2} placeholder="Observaciones adicionales..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none" />
            </div>

            {error && <p className="col-span-2 text-red-500 text-sm">{error}</p>}

            <div className="col-span-2 flex gap-2 justify-end pt-2 border-t border-gray-100">
              <button type="button" onClick={() => setShowModal(false)}
                className="px-4 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                Cancelar
              </button>
              <button type="submit" disabled={saving}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700 disabled:opacity-60">
                {saving ? 'Guardando...' : editingId != null ? 'Actualizar' : 'Guardar prospecto'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {bitacoraProspecto && (
        <Modal title={`Bitácora — ${bitacoraProspecto.nombre}`} onClose={() => setBitacoraProspecto(null)}>
          {bitacora === null ? (
            <p className="text-sm text-gray-400 text-center py-4">Cargando...</p>
          ) : bitacora.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">Sin seguimientos registrados</p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {bitacora
                .slice()
                .sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime())
                .map((s: any) => (
                  <div key={s.id} className="border-l-4 border-l-primary-300 pl-3 py-1">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="flex items-center">{s.tipo === 'Llamada'
                        ? (s.resultado === 'Contestada'
                          ? <Phone size={14} className="text-green-600" />
                          : s.resultado === 'No contestó'
                            ? <PhoneOff size={14} className="text-red-600" />
                            : <Phone size={14} className="text-gray-500" />)
                        : '📝'}</span>
                      <span className="font-medium text-gray-800">{s.tipo}</span>
                      {s.resultado && <span className="text-gray-500">— {s.resultado}</span>}
                    </div>
                    <p className="text-xs text-gray-400">
                      {new Date(s.fecha).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}
                    </p>
                    {s.notas && <p className="text-xs text-gray-600 mt-0.5">{s.notas}</p>}
                  </div>
                ))}
            </div>
          )}
        </Modal>
      )}

      {conversionProspecto && (
        <Modal title={`Convertir a cliente — ${conversionProspecto.nombre}`}
          onClose={() => { setConversionProspecto(null); setProspectos(prev => [...prev]); }}>
          <form onSubmit={submitConversion} className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Producto *</label>
              <select required value={conversionForm.producto}
                onChange={e => setConversionForm(f => ({ ...f, producto: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="">Seleccionar...</option>
                {ramos.map((r: any) => <option key={r.id} value={r.nombre}>{r.icono} {r.nombre}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Número de póliza</label>
              <input type="text" value={conversionForm.numeroPolizaCliente}
                onChange={e => setConversionForm(f => ({ ...f, numeroPolizaCliente: e.target.value }))}
                placeholder="Opcional, si ya se emitió"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div className="flex gap-2 justify-end pt-2 border-t border-gray-100">
              <button type="button"
                onClick={() => { setConversionProspecto(null); setProspectos(prev => [...prev]); }}
                className="px-4 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                Cancelar
              </button>
              <button type="submit" disabled={savingConversion}
                className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-60">
                {savingConversion ? 'Guardando...' : '✅ Confirmar conversión'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
